import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
// ✅ Added Storage imports
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import BackHomeButton from '../components/BackHomeButton';

function Profile() {
  const [name, setName] = useState('');
  const [school, setSchool] = useState('');
  const [board, setboard] = useState('');
  const [standard, setStandard] = useState('');
  const [city, setCity] = useState('');
  const [user, setUser] = useState(null);

  // ✅ NEW STATES
  const [bio, setBio] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [supportedStandards, setSupportedStandards] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const [cityOptions, setCityOptions] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);

  const navigate = useNavigate();
  const [persona, setPersona] = useState('');
  const location = useLocation();

  useEffect(() => {
    const state = location.state;
    if (state) {
      if (state.persona) setPersona(state.persona);
    }
  }, [location.state]);

  const handleLogout = async () => {
    const confirmLogout = window.confirm('Are you sure you want to log out?');
    if (!confirmLogout) return;
  
    try {
      await auth.signOut();
      alert('You have been logged out.');
      navigate('/login');
    } catch (error) {
      alert('Error logging out: ' + error.message);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate('/login');
      } else {
        setUser(currentUser);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchCitySchools = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'city_schools_prod'));
        const data = querySnapshot.docs.map(doc => doc.data());

        const cities = [...new Set(data.map(item => item.city))]
  .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

        setCityOptions(cities);

        if (cities.length > 0) {
          const defaultCity = cities[0];
          setCity(defaultCity);
const schoolsForCity = data
  .filter(item => item.city === city)
  .map(item => item.school)
  .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

          setFilteredSchools(schoolsForCity);
        }
      } catch (error) {
        console.error("Error fetching city-school data:", error);
      }
    };
    fetchCitySchools();
  }, []);

  useEffect(() => {
    if (!city) return;
    const fetchSchoolsForCity = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'city_schools_prod'));
        const data = querySnapshot.docs.map(doc => doc.data());
        const schoolsForCity = data
          .filter(item => item.city === city)
          .map(item => item.school)
          .sort();
        setFilteredSchools(schoolsForCity);
        setSchool('');
      } catch (error) {
        console.error("Error fetching schools for city:", error);
      }
    };
    fetchSchoolsForCity();
  }, [city]);

  const handleToggleStandard = (std) => {
    setSupportedStandards(prev => 
      prev.includes(std) ? prev.filter(s => s !== std) : [...prev, std]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert("User not logged in");
      return;
    }

    setIsUploading(true);

    let uploadedAvatarUrl = "";

    try {
      // ✅ 1. Upload Avatar if selected
      if (avatarFile) {
        const storage = getStorage();
        const fileRef = ref(storage, `avatars/${user.uid}_${Date.now()}`);
        await uploadBytes(fileRef, avatarFile);
        uploadedAvatarUrl = await getDownloadURL(fileRef);
      }

      // 2. THE PRIVATE DATA
      const privateProfileData = {
        name,
        age: 0,
        gender: 'NA',
        school,
        standard,
        city,
        email: user.email,
        uid: user.uid,
        created_dt: new Date(),
        updated_dt: new Date(),
        persona: persona,
        board: board,
        bio: bio,
        avatarUrl: uploadedAvatarUrl,
        supportedStandards: persona === 'Teacher' ? supportedStandards : []
      };

      // 3. THE PUBLIC DATA
      const publicProfileData = {
        displayName: name,
        role: persona,
        city: city,
        school: school,
        standard: standard,
        board: board,
        email: user.email,
        bio: bio,
        avatarUrl: uploadedAvatarUrl,
        supportedStandards: persona === 'Teacher' ? supportedStandards : [],
        updated_dt: new Date()
      };

      // 4. THE DUAL WRITE
      await Promise.all([
        setDoc(doc(db, 'profiles', user.uid), privateProfileData, { merge: true }),
        setDoc(doc(db, 'public_profiles', user.uid), publicProfileData, { merge: true })
      ]);

      // Check and update city_schools_prod
      const citySchoolsSnap = await getDocs(collection(db, 'city_schools_prod'));
      const existingPairs = citySchoolsSnap.docs.map(doc => doc.data());

      const schoolExistsInCity = existingPairs.some(
        (entry) => entry.city === city && entry.school === school
      );

      if (!schoolExistsInCity) {
        await setDoc(doc(db, 'city_schools_prod', `${city}_${school}`), {
          city,
          school,
        });
      }

      alert("Profile and Public Storefront created!");
      navigate('/Old_Home', { state: { persona } });
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Something went wrong.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={styles.page}>
      <h2 style={styles.heading}>Create Profile</h2>
      <form onSubmit={handleSubmit} style={styles.form}>

        <label style={styles.label}>Logged In as: {persona}</label>
        <br />

        {/* ✅ AVATAR UPLOAD */}
        <label style={styles.label}>Profile Picture:</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setAvatarFile(e.target.files[0])}
          style={styles.input}
        />

        {/* ✅ BIO TEXT AREA */}
        <label style={styles.label}>Bio (Optional):</label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Tell us a little about yourself..."
          style={{ ...styles.input, height: '80px', resize: 'vertical' }}
          maxLength={150}
        />
        <span style={{ fontSize: '10px', color: '#888', marginTop: '-15px', marginBottom: '15px', textAlign: 'right' }}>
          {bio.length}/150
        </span>

        <label style={styles.label}>Name:</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          required
          style={styles.input}
        />

        <label style={styles.label}>City:</label>
        <select
          style={styles.input}
          value={city}
          onChange={(e) => setCity(e.target.value)}
          required
        >
          <option value="">Select a city</option>
          {cityOptions.map((c, idx) => (
            <option key={idx} value={c}>{c}</option>
          ))}
        </select>

        <label style={styles.label}>School:</label>
        <select
          style={styles.input}
          value={school}
          onChange={(e) => setSchool(e.target.value)}
          required
        >
          <option value="">Select a school</option>
          {filteredSchools.map((s, idx) => (
            <option key={idx} value={s}>{s}</option>
          ))}
        </select>

        <label style={styles.label}>Board:</label>
        <select
          style={styles.input}
          value={board}
          onChange={(e) => setboard(e.target.value)}
          required
        >
          <option value="">Select a Board</option>
          <option value="ICSE">ICSE</option>
          <option value="CBSE">CBSE</option>
          <option value="OTHERSTATE">Other State Boards</option>
        </select>

        <div style={{ marginTop: '10px', marginBottom: '15px' }}>
          <label style={{ ...styles.label, display: 'block' }}>
            Don’t see your school, city or board listed?
          </label>
          <button
            type="button"
            style={{ ...styles.button, backgroundColor: '#f44336', padding: '0.5rem', fontSize: '0.9rem' }}
            onClick={() => alert("Please raise a support ticket by contacting us via email or help form.")}
          >
            Raise Support Ticket
          </button>
        </div>

        <label style={styles.label}>{persona === 'Teacher' ? 'Primary Standard:' : 'Standard:'}</label>
        <select
          value={standard}
          onChange={(e) => setStandard(e.target.value)}
          required
          style={styles.select}
        >
          <option value="">Select Standard</option>
          <option value="LKG">LKG</option>
          <option value="UKG">UKG</option>
          {[...Array(12)].map((_, i) => (
            <option key={i + 1} value={i + 1}>{i + 1}</option>
          ))}
        </select>

        {/* ✅ MULTI-SELECT STANDARDS (TEACHERS ONLY) */}
        {persona === 'Teacher' && (
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={styles.label}>Other Standards Supported:</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '5px' }}>
              {[...Array(12)].map((_, i) => (
                <label key={i + 1} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    checked={supportedStandards.includes(i + 1)}
                    onChange={() => handleToggleStandard(i + 1)}
                  />
                  Std {i + 1}
                </label>
              ))}
            </div>
          </div>
        )}

        <button type="submit" style={styles.button} disabled={isUploading}>
          {isUploading ? "Uploading..." : "Save Profile"}
        </button>
      </form>

      <br></br>

      <BackHomeButton style={{ marginTop: '2rem' }} backState={{ persona }} />

      <div style={{ textAlign: 'left', paddingRight: '1rem', marginTop: '1rem' }}>
        <button style={{ ...styles.button, backgroundColor: '#666' }} onClick={handleLogout}>
          Logout
        </button>
      </div>

      <footer style={styles.footer}>
        <p style={styles.footerText}>© 2026 Eascode All Rights Reserved.</p>
      </footer>
    </div>
  );
}

const styles = {
  page: { maxWidth: '480px', margin: '2rem auto', padding: '1.5rem 2rem', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 14px rgba(0,0,0,0.1)', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
  heading: { textAlign: 'center', marginBottom: '1.8rem', color: '#333', fontWeight: '700', fontSize: '1.9rem' },
  form: { display: 'flex', flexDirection: 'column' },
  label: { marginBottom: '0.3rem', fontWeight: '600', color: '#555' },
  input: { padding: '0.6rem 0.9rem', fontSize: '1rem', borderRadius: '8px', border: '1.5px solid #bbb', marginBottom: '1.25rem' },
  select: { padding: '0.6rem 0.9rem', fontSize: '1rem', borderRadius: '8px', border: '1.5px solid #bbb', marginBottom: '1.25rem', backgroundColor: '#fff' },
  button: { padding: '0.75rem', fontSize: '1.1rem', fontWeight: '600', backgroundColor: '#4285f4', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer' },
  footer: { padding: '1rem', backgroundColor: '#333', color: '#fff', textAlign: 'center', marginTop: '3rem' },
  footerText: { fontSize: '0.9rem' },
};

export default Profile;